import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import type { MixMatchQuestion as MixMatchType } from '@/types/question-types';
import { renderMathInText } from '@/utils/renderMath';
import styles from './MixMatchQuestion.module.scss';

interface MixMatchQuestionProps {
  question: MixMatchType;
  userAnswer?: Array<{ leftId: string; rightId: string }>;
  onAnswer: (answer: Array<{ leftId: string; rightId: string }>) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
}

export function MixMatchQuestion({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
}: MixMatchQuestionProps) {
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [selectedRight, setSelectedRight] = useState<string | null>(null);
  const [userPairs, setUserPairs] = useState<Array<{ leftId: string; rightId: string }>>(
    userAnswer || []
  );

  // Update pairs when userAnswer prop changes
  useEffect(() => {
    if (userAnswer) {
      setUserPairs(userAnswer);
    }
  }, [userAnswer]);

  const handleLeftClick = (id: string) => {
    if (isReviewMode) return;

    // If this item is already paired, don't allow selection
    if (userPairs.some(pair => pair.leftId === id)) {
      return;
    }

    setSelectedLeft(id);

    // If we have a right item selected, create a pair
    if (selectedRight && !userPairs.some(pair => pair.rightId === selectedRight)) {
      addPair(id, selectedRight);
      setSelectedLeft(null);
      setSelectedRight(null);
    }
  };

  const handleRightClick = (id: string) => {
    if (isReviewMode) return;

    // If this item is already paired, don't allow selection
    if (userPairs.some(pair => pair.rightId === id)) {
      return;
    }

    setSelectedRight(id);

    // If we have a left item selected, create a pair
    if (selectedLeft && !userPairs.some(pair => pair.leftId === selectedLeft)) {
      addPair(selectedLeft, id);
      setSelectedLeft(null);
      setSelectedRight(null);
    }
  };

  const addPair = (leftId: string, rightId: string) => {
    const newPairs = [...userPairs, { leftId, rightId }];
    setUserPairs(newPairs);
    onAnswer(newPairs);
  };

  const removePair = (index: number) => {
    if (isReviewMode) return;

    const newPairs = userPairs.filter((_, idx) => idx !== index);
    setUserPairs(newPairs);
    onAnswer(newPairs);
  };

  const isPairCorrect = (pair: { leftId: string; rightId: string }): boolean => {
    return question.content.correctPairs.some(
      cp => cp.leftId === pair.leftId && cp.rightId === pair.rightId
    );
  };

  const getLeftText = (id: string): string => {
    return question.content.leftColumn.find(item => item.id === id)?.text || '';
  };

  const getRightText = (id: string): string => {
    return question.content.rightColumn.find(item => item.id === id)?.text || '';
  };

  const getLeftItemStatus = (id: string): 'default' | 'selected' | 'paired' => {
    if (userPairs.some(pair => pair.leftId === id)) return 'paired';
    if (selectedLeft === id) return 'selected';
    return 'default';
  };

  const getRightItemStatus = (id: string): 'default' | 'selected' | 'paired' => {
    if (userPairs.some(pair => pair.rightId === id)) return 'paired';
    if (selectedRight === id) return 'selected';
    return 'default';
  };

  return (
    <div className={styles.mixMatch}>
      <div className={styles.mixMatch__question}>
        <h3 className={styles.mixMatch__questionText}>
          {renderMathInText(question.question_text)}
        </h3>
        <p className={styles.mixMatch__hint}>
          Click an item from the left column, then click the matching item from the right column to create a pair
        </p>
      </div>

      <div className={styles.mixMatch__columns}>
        <div className={styles.mixMatch__column}>
          <h4 className={styles.mixMatch__columnTitle}>Items</h4>
          <div className={styles.mixMatch__items}>
            {question.content.leftColumn.map((item, index) => {
              const status = getLeftItemStatus(item.id);
              return (
                <motion.button
                  key={item.id}
                  className={`${styles.mixMatch__item} ${styles[`mixMatch__item--${status}`]
                    }`}
                  onClick={() => handleLeftClick(item.id)}
                  disabled={isReviewMode || status === 'paired'}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={!isReviewMode && status !== 'paired' ? { scale: 1.02 } : {}}
                  whileTap={!isReviewMode && status !== 'paired' ? { scale: 0.98 } : {}}
                >
                  {renderMathInText(item.text)}
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className={styles.mixMatch__column}>
          <h4 className={styles.mixMatch__columnTitle}>Matches</h4>
          <div className={styles.mixMatch__items}>
            {question.content.rightColumn.map((item, index) => {
              const status = getRightItemStatus(item.id);
              return (
                <motion.button
                  key={item.id}
                  className={`${styles.mixMatch__item} ${styles[`mixMatch__item--${status}`]
                    }`}
                  onClick={() => handleRightClick(item.id)}
                  disabled={isReviewMode || status === 'paired'}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={!isReviewMode && status !== 'paired' ? { scale: 1.02 } : {}}
                  whileTap={!isReviewMode && status !== 'paired' ? { scale: 0.98 } : {}}
                >
                  {renderMathInText(item.text)}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {userPairs.length > 0 && (
        <div className={styles.mixMatch__pairsSection}>
          <h4 className={styles.mixMatch__pairsTitle}>Your Matches:</h4>
          <div className={styles.mixMatch__pairs}>
            {userPairs.map((pair, idx) => {
              const isCorrect = showExplanation ? isPairCorrect(pair) : null;
              return (
                <motion.div
                  key={idx}
                  className={`${styles.mixMatch__pair} ${isCorrect === true
                    ? styles['mixMatch__pair--correct']
                    : isCorrect === false
                      ? styles['mixMatch__pair--incorrect']
                      : ''
                    }`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <div className={styles.mixMatch__pairContent}>
                    <span className={styles.mixMatch__pairLeft}>
                      {renderMathInText(getLeftText(pair.leftId))}
                    </span>
                    <span className={styles.mixMatch__pairArrow}>↔</span>
                    <span className={styles.mixMatch__pairRight}>
                      {renderMathInText(getRightText(pair.rightId))}
                    </span>
                  </div>
                  {!isReviewMode && (
                    <button
                      className={styles.mixMatch__pairRemove}
                      onClick={() => removePair(idx)}
                      aria-label="Remove pair"
                    >
                      ×
                    </button>
                  )}
                  {showExplanation && isCorrect && (
                    <div className={styles.mixMatch__correctBadge}>✓</div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {showExplanation && (
        <>
          <div className={styles.mixMatch__correctPairs}>
            <h4 className={styles.mixMatch__correctPairsTitle}>Correct Matches:</h4>
            <div className={styles.mixMatch__pairs}>
              {question.content.correctPairs.map((pair, idx) => (
                <div key={idx} className={styles.mixMatch__correctPair}>
                  <span className={styles.mixMatch__pairLeft}>
                    {renderMathInText(getLeftText(pair.leftId))}
                  </span>
                  <span className={styles.mixMatch__pairArrow}>↔</span>
                  <span className={styles.mixMatch__pairRight}>
                    {renderMathInText(getRightText(pair.rightId))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            className={styles.mixMatch__explanation}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.mixMatch__explanationHeader}>
              <strong>Explanation:</strong>
            </div>
            <p className={styles.mixMatch__explanationText}>
              {renderMathInText(question.explanation)}
            </p>
          </motion.div>
        </>
      )}
    </div>
  );
}
